import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import {
  BowlingParticipant,
  BowlingState,
  BowlingMode,
  BowlingPhase
} from '../../../interfaces/bowling.interface';
import { BowlingSceneComponent } from './bowling-scene.component';

/** Banner kinds dispatched by `playBannerSound` and rendered with
 *  per-kind colour treatment in the template:
 *    - `'strike'` (10 pins) — gold + applause SFX + "STRIKE!" sub
 *    - `'gutter'` (0 pins)  — grey + wah-wah SFX + "GUTTER" label
 *    - `'good'`   (1–9)     — pink/magenta + no SFX + "PINS" label
 *    - `null`               — no banner shown */
type Banner = null | 'strike' | 'gutter' | 'good';

/**
 * Host shell for the bowling mini-game. Composes the entire bowling
 * UX:
 *   - the 3D scene (`<app-bowling-scene>`) — canvas + physics, owns
 *     ball control and emits `rollStarted` / `rollComplete`
 *   - the live scoreboard overlay (two `<app-bowling-scorecard>`)
 *   - the in-scene CRT panel data feed (`scoreboardData`)
 *   - all post-roll UI: pin-count banner, "NEXT UP" boxing card
 *   - chrome controls: How-to-Play button + modal, immersive fullscreen
 *     toggle, ball-skin info — and the page-level immersive class on
 *     `.bowling-game`
 *
 * **Data ownership**: the actual game state (`BowlingState`) lives in
 * `MiniGameService.session$` — this component only **reads** it via
 * the `state` / `currentSide` / `currentStudent` getters and writes
 * results back through `MiniGameService.recordBowlingRoll`.
 *
 * **Lifecycle**:
 *   - `ngOnInit` — subscribe to language + session, auto-open the
 *     How-to-Play modal on first visit.
 *   - `onSceneReady` — bumps `rackTick` so the scene renders the
 *     initial pin rack as soon as it finishes async loading.
 *   - `onRollStarted` / `onRollComplete` — drive the post-roll
 *     sequence (banner → next-up card → rack reset).
 *   - `ngOnDestroy` — tear down RxJS subs.
 */
@Component({
  selector: 'app-bowling-game',
  templateUrl: './bowling-game.component.html',
  styleUrls: ['./bowling-game.component.scss']
})
export class BowlingGameComponent implements OnInit, OnDestroy {
  /** Handle to the child 3D scene — used to call `showHints()` after
   *  the user closes the How-to-Play modal so the in-scene swipe/tap
   *  cues reappear. */
  @ViewChild(BowlingSceneComponent) scene!: BowlingSceneComponent;

  /** Cleanup pipe for all `takeUntil`-chained subscriptions. */
  private destroy$ = new Subject<void>();
  /** Active app language ('ar' / 'en'); fed to every child component
   *  for bilingual labels. */
  currLang: any = 'en';

  /** Active MiniGameSession from the service — null between sessions
   *  (e.g. after `finishMiniGame` clears it). The whole template is
   *  guarded by `*ngIf="state"`. */
  session: MiniGameSession | null = null;
  /** Re-export so the template can reference `BowlingMode.ONE_VS_ONE`
   *  etc. via `BowlingMode.X` syntax. */
  BowlingMode = BowlingMode;

  /** True from `rollStarted` until `rollComplete`. Currently used as
   *  a stat / flag — not directly bound in the template. */
  rollInFlight = false;
  /** Monotonically-increasing tick — incremented on `onSceneReady`
   *  and at the end of each post-roll sequence. The scene watches
   *  this `@Input()` for change and re-racks pins + resets the ball
   *  on every bump. Cheap "trigger me" pattern that avoids an
   *  imperative @ViewChild call from this component. */
  rackTick = 0;
  /** Current banner kind (null = no banner). Set in `onRollComplete`,
   *  cleared after 1.4 s by the post-roll setTimeout chain. */
  banner: Banner = null;
  /** Pins knocked on the most recent roll — drives the giant numeral
   *  in the banner template. */
  lastRollPins = 0;
  /** Bound to scene's `inputDisabled @Input()`. Held true through
   *  the entire post-roll sequence (banner + NEXT-UP card + rack
   *  reset, ~6.4 s total) so a stray tap can't fire a second roll. */
  inputDisabled = false;
  /** Boxing-ring "NEXT UP" card visibility — true for 5 s between
   *  the pin-count banner and the next bowler's roll, unless the game
   *  just finished (in which case the summary takes over). */
  showNextBowler = false;

  /** localStorage key — set on first close of the How-to-Play modal
   *  to suppress the auto-open on subsequent visits. The "?" button
   *  ignores this and re-opens unconditionally. */
  private static readonly HOW_TO_PLAY_KEY = 'bowling.howToPlaySeen';
  /** How-to-play modal visibility. Auto-set to true on first visit
   *  via the `ngOnInit` localStorage check; explicitly toggled by
   *  `openHowToPlay` / `closeHowToPlay`. */
  howToPlayOpen = false;

  // Immersive / "in-app fullscreen" state — drives the `.is-immersive`
  // class on `.bowling-game` (CSS overlay covering the page chrome)
  // and the toggle button's icon. Defaults to **true** so the bowling
  // box covers the screen the moment the game loads — the player
  // doesn't have to hunt for the toggle to get the immersive view.
  // Browser fullscreen is only requested on an explicit user click
  // (it would be blocked without a gesture if we tried on init).
  isFullscreen = true;

  constructor(
    /** Source of `session$`; receives `recordBowlingRoll` calls. */
    private miniGameService: MiniGameService,
    /** Used for the strike-applause + gutter-wahwah banner SFX. */
    private gameAudioService: GameAudioService,
    /** Active app language stream. */
    private globalService: GlobalService
  ) {}

  /**
   * Subscribe to language + session streams and check the
   * onboarding-seen flag. Both subscriptions are torn down via
   * `destroy$` in `ngOnDestroy`.
   *
   * The session subscription is what keeps `state`, `currentSide`,
   * `currentStudent`, etc. fresh — every CD pass re-evaluates those
   * getters off the latest snapshot.
   */
  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => this.session = s);

    // Auto-open onboarding the first time a user enters the game.
    // Subsequent visits skip the overlay; user can re-open via the
    // floating "?" button. Wrapped in try/catch because localStorage
    // throws in private-browsing modes / sandboxed iframes.
    try {
      if (!localStorage.getItem(BowlingGameComponent.HOW_TO_PLAY_KEY)) {
        this.howToPlayOpen = true;
      }
    } catch { /* localStorage unavailable — skip auto-show */ }
  }

  /** Open the onboarding modal explicitly — wired to the floating
   *  "?" button. Does NOT touch the seen-flag (only `closeHowToPlay`
   *  writes it). */
  openHowToPlay(): void {
    this.howToPlayOpen = true;
  }

  /**
   * Close the modal. Two side-effects beyond hiding the modal:
   *
   *   1. Persists the `howToPlaySeen` flag so the modal doesn't
   *      auto-open next session.
   *   2. Re-enables the in-scene swipe / tap hints by calling
   *      `scene.showHints()`. The player just watched the explainer
   *      — showing the cues reinforces the same gestures on the
   *      actual ball. The cues auto-dismiss again on the next pin
   *      hit (so they don't permanently clutter the scene).
   */
  closeHowToPlay(): void {
    this.howToPlayOpen = false;
    try { localStorage.setItem(BowlingGameComponent.HOW_TO_PLAY_KEY, '1'); } catch { /* ignore */ }
    this.scene?.showHints();
  }

  // ──────────────────────────────────────────────────────────
  // Fullscreen toggle
  // ──────────────────────────────────────────────────────────

  /**
   * Toggle "immersive" mode — the `.bowling-game` container becomes
   * `position: fixed; inset: 0` overlaying everything else (game
   * world background, Select Game button, Back-to-Calling-Screen
   * button, etc.) so just the game is on screen. Best-effort browser
   * fullscreen is also requested so the OS chrome (tabs, address bar)
   * hides too — but the in-app overlay is the source of truth and
   * works even if the browser denies the FS request.
   */
  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      this.tryRequestBrowserFullscreen();
    } else {
      this.tryExitBrowserFullscreen();
    }
  }

  private tryRequestBrowserFullscreen(): void {
    const el = document.documentElement as any;
    const req: any = el.requestFullscreen
      || el.webkitRequestFullscreen
      || el.mozRequestFullScreen
      || el.msRequestFullscreen;
    if (typeof req === 'function') {
      try { req.call(el); } catch { /* user gesture / permission denied */ }
    }
  }

  private tryExitBrowserFullscreen(): void {
    const doc = document as any;
    const inFs = !!(doc.fullscreenElement || doc.webkitFullscreenElement
      || doc.mozFullScreenElement || doc.msFullscreenElement);
    if (!inFs) return;  // already out
    const exit: any = doc.exitFullscreen
      || doc.webkitExitFullscreen
      || doc.mozCancelFullScreen
      || doc.msExitFullscreen;
    if (typeof exit === 'function') {
      try { exit.call(document); } catch { /* ignore */ }
    }
  }

  // Sync the in-app immersive flag if the user presses Esc to exit
  // browser fullscreen — keeps the icon + overlay from getting stuck
  // in the "expanded" state when the OS chrome is back.
  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  onFullscreenChange(): void {
    const doc = document as any;
    const inFs = !!(
      doc.fullscreenElement
      || doc.webkitFullscreenElement
      || doc.mozFullScreenElement
      || doc.msFullscreenElement
    );
    if (!inFs && this.isFullscreen) {
      this.isFullscreen = false;
    }
  }

  ngOnDestroy(): void {
    // Tear down the language + session subscriptions chained off
    // destroy$. The `@HostListener` on document:fullscreenchange is
    // auto-cleaned by Angular when the component is destroyed.
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Type-narrowed `gameState` from the active session. Returns null
   *  while the session is still resolving on init. Every other
   *  getter starts from this. */
  get state(): BowlingState | null {
    return (this.session?.gameState as BowlingState) ?? null;
  }

  /** The side that's currently up to bowl. Reads `state.currentSide`
   *  (0 or 1) and indexes into `participants`. After
   *  `recordBowlingRoll` advances the turn, this getter returns the
   *  *next* bowler — which is what the NEXT-UP card displays. */
  get currentSide(): BowlingParticipant | null {
    if (!this.state) return null;
    return this.state.participants[this.state.currentSide];
  }

  /** The specific student bowling next on the active side. Index
   *  wraps modulo team size so team mode rotates through every
   *  student over multiple frames. Returns null if the side has no
   *  students (defensive — should never happen in valid sessions). */
  get currentStudent(): MiniGameStudent | null {
    const p = this.currentSide;
    if (!p || p.students.length === 0) return null;
    return p.students[p.currentStudentIdx % p.students.length] ?? null;
  }

  /** Bilingual student-name pick — used by the NEXT-UP card and the
   *  header bowler chip. Returns '' if there's no student. */
  get currentStudentName(): string {
    const s = this.currentStudent;
    if (!s) return '';
    return this.currLang === 'ar' ? s.name.ar : s.name.en;
  }

  /** Header chip label — "Team Name · Student Name" in team mode,
   *  just the team name in 1v1 mode (since the team IS the player). */
  get currentTurnLabel(): string {
    const p = this.currentSide;
    if (!p) return '';
    const name = this.currLang === 'ar' ? p.displayName.ar : p.displayName.en;
    return this.state?.mode === BowlingMode.ONE_VS_ONE ? name : `${name} · ${this.currentStudentName}`;
  }

  /** 10-bool mask of which pins should stand at the next roll's
   *  rack. Always all-true in our model (each roll faces a fresh
   *  rack). Bound to scene's `pinsStanding` input which runs through
   *  `restorePinsFromMask` on each `rackTick`. */
  get pinsStanding(): boolean[] {
    return this.state?.pinsStandingBefore ?? Array(10).fill(true);
  }

  /**
   * Live data fed to the in-scene CRT score panel above the pin
   * deck. Two-team summary that the scene paints into a
   * CanvasTexture every ~120 ms via `redrawScorePanel`. The setter
   * inside the scene also fires an immediate redraw so the score
   * updates the same frame the player's roll lands.
   */
  get scoreboardData(): Array<{ name: string; score: number; color: string }> | null {
    if (!this.state) return null;
    return this.state.participants.map(p => ({
      name: this.currLang === 'ar' ? p.displayName.ar : p.displayName.en,
      score: p.totalScore,
      color: p.color
    }));
  }

  /**
   * Scene → host event: scene finished async loading + rendered its
   * first frame. Bumping `rackTick` triggers `restorePinsFromMask`
   * inside the scene's main loop, which puts the initial 10 pins in
   * place and snaps the ball to its start position. Without this
   * the scene would show an empty lane until the first roll. */
  onSceneReady(): void {
    this.rackTick++;
  }

  /** Scene → host event: ball just left the foul line. Marks the
   *  flag and clears any leftover banner from a previous roll. */
  onRollStarted(): void {
    this.rollInFlight = true;
    this.banner = null;
  }

  onRollComplete(event: { pinsKnocked: number; knockedIndices: number[] }): void {
    this.rollInFlight = false;
    this.lastRollPins = event.pinsKnocked;

    this.banner = this.classifyBanner(event.pinsKnocked);
    this.playBannerSound(this.banner);

    // Recording the roll advances the BowlingState (currentSide flips,
    // currentStudentIdx may bump, currentFrame may bump). After this
    // call, `currentSide` / `currentStudent` getters return the *next*
    // bowler — which is what the "NEXT UP" card displays.
    this.miniGameService.recordBowlingRoll(event.pinsKnocked, event.knockedIndices);

    // Three-stage post-roll sequence:
    //   1. Pin-count banner (1.4 s) — already on screen.
    //   2. "NEXT UP" boxing-ring card (5 s) — announces who shoots
    //      next. Skipped on the final roll since the summary takes
    //      over instead.
    //   3. rackTick — pins re-rack, ball returns, next bowler can play.
    this.inputDisabled = true;
    setTimeout(() => {
      this.banner = null;
      const gameOver = this.state?.phase === BowlingPhase.FINISHED;
      if (gameOver) {
        // No card on the last roll — MiniGameSummary takes over.
        this.inputDisabled = false;
        this.rackTick++;
        return;
      }
      this.showNextBowler = true;
      setTimeout(() => {
        this.showNextBowler = false;
        this.inputDisabled = false;
        this.rackTick++;
      }, 5000);
    }, 1400);
  }

  private playBannerSound(banner: Banner): void {
    switch (banner) {
      case 'strike': {
        // Source clip is ~9.6 s — way too long for a per-strike
        // celebration sting. The mini-game-summary screen still uses
        // the full clip for the game-end fanfare; here we just
        // truncate the playback to ~2 s. Source MP3 stays shared.
        const audio = this.gameAudioService.play(
          'assets/game-mode/sound-effects/applauseSoundEffect.mp3'
        );
        if (audio) {
          setTimeout(() => this.gameAudioService.stop(audio), 3000);
        }
        break;
      }
      case 'gutter':
        this.gameAudioService.play('assets/game-mode/sound-effects/wahWahSoundEffect.mp3');
        break;
      case 'good':
      default:
        break;
    }
  }

  /**
   * Single-roll banner classification — no spare logic since each bowler
   * gets exactly one roll per frame. Every roll now shows a banner so
   * the player always sees their pin count in the big-number visual:
   *   - 10 pins → strike (gold styling, "STRIKE!" sub-label)
   *   - 0 pins  → gutter (grey styling, "GUTTER" label)
   *   - 1–9     → good   (pink styling, "PINS" label)
   */
  private classifyBanner(pins: number): Banner {
    if (pins === 10) return 'strike';
    if (pins === 0) return 'gutter';
    return 'good';
  }

  // (bannerText getter removed — the new big-number banner reads
  //  the count + per-kind label inline in the template, no longer
  //  needs a single text string per banner kind.)
}
